/**
 * Rock-quarter ordering helpers.
 *
 * Used on Initiative Detail and the Executive sub-list to sort
 * supporting projects by which Q's rock they support (Q1 → Q4 →
 * unset). Parent/child relationships are preserved: a child stays
 * glued to its parent in the sorted output, and groups are sorted
 * by the parent's rock_quarter.
 */

const QUARTER_ORDER: Record<string, number> = {
  Q1: 1,
  Q2: 2,
  Q3: 3,
  Q4: 4,
};

/** Lower value = sorts earlier. Unset goes last (99). */
export function quarterSortKey(quarter: string | null | undefined): number {
  if (!quarter) return 99;
  return QUARTER_ORDER[quarter] ?? 98;
}

/** Sort a flat parent+children list by rock_quarter, keeping each
 *  child adjacent to its parent. Stable: ties preserve input order. */
export function sortProjectsByRockQuarter<
  P extends {
    id: string;
    parent_project_id?: string | null;
    rock_quarter?: string | null;
  },
>(projects: P[]): P[] {
  // Walk the input building groups [parent, ...children]. Anything
  // without a parent_project_id starts a new group.
  type Group = { parent: P; rest: P[]; origIndex: number };
  const groups: Group[] = [];
  let current: Group | null = null;
  let origIdx = 0;
  for (const p of projects) {
    if (!p.parent_project_id) {
      if (current) groups.push(current);
      current = { parent: p, rest: [], origIndex: origIdx };
    } else if (current) {
      current.rest.push(p);
    } else {
      // Orphan child (parent not in this list). Treat as its own group
      // so it doesn't get lost.
      groups.push({ parent: p, rest: [], origIndex: origIdx });
    }
    origIdx++;
  }
  if (current) groups.push(current);

  // Stable sort: tie-break on origIndex so equal quarters keep their
  // original ordering.
  groups.sort((a, b) => {
    const ka = quarterSortKey(a.parent.rock_quarter);
    const kb = quarterSortKey(b.parent.rock_quarter);
    if (ka !== kb) return ka - kb;
    return a.origIndex - b.origIndex;
  });

  // Flatten back to [parent, ...children, parent, ...children, ...]
  const out: P[] = [];
  for (const g of groups) {
    out.push(g.parent);
    out.push(...g.rest);
  }
  return out;
}

/**
 * isRock — a project is a "rock" when it's tagged with a Rock Quarter
 * (Q1–Q4). Per CFO direction (2026-06) rocks are supporting TASKS of a
 * key initiative, not standalone projects. They're hidden from every
 * project-list surface (Portfolio, Phase Board, Project Gantt,
 * Portfolio Health) and excluded from capacity math — they only show
 * on the Initiatives Gantt, Roadmap, and as supporting items on the
 * project / initiative detail pages.
 *
 * Single source of truth for the rule so every surface filters
 * identically.
 */
export function isRock(p: { rock_quarter?: string | null }): boolean {
  return !!(p.rock_quarter && p.rock_quarter.trim());
}
